import { useInfiniteQuery, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import last from 'lodash/last';
import {
  bulkAcceptBookkeepingTransactions,
  bulkRegisterBookkeepingTransactions,
  createBookkeepingTransaction,
  deleteBookkeepingTransaction,
  fetchBookkeepingTransaction,
  fetchBookkeepingTransactions,
  fetchBookkeepingEntriesMetadata,
  updateBookkeepingTransaction,
} from '~/api/bookkeeping/bookkeeping-transactions';
import { queryClient } from '~/client/queryClient';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import {
  BookkeepingBankTransaction,
  BookkeepingBankTransactionsKeys,
  BookkeepingLedgerTransaction,
  BookkeepingTransaction,
  BookkeepingTransactionFilters,
  BookkeepingTransactionQueryKeys,
  BookkeepingTransactionSorting,
  BulkRegisterBookkeepingTransactionsPayload,
  CreateBookkeepingTransaction,
  LedgerCardsSection,
  UpdateBookkeepingTransaction,
} from '~/types/general-bookkeeping';
import { Money } from '~/types/money';
import { TransactionsFiltersFields, TransactionsKeys } from '~/types/transaction';
import { isDefined } from '~/types/typeGuards';
import { isAvere, isDare } from '~/utils/bookkeeping/bookkeeping-entry';
import { SortInfo } from '~/utils/jsonapi-query';
import { absMoney, newMoney, subtractMoney, sumMoney } from '~/utils/money';

export const useBookkeepingTransaction = (id: BookkeepingTransaction['id'] | null) =>
  useSuspenseQuery({
    queryKey: BookkeepingTransactionQueryKeys.item(id || ''),
    queryFn: () =>
      id
        ? fetchBookkeepingTransaction(id, {
            include: [
              'company',
              'bookkeepingEntries',
              'bookkeepingEntries.bookkeepingAccount',
              'bookkeepingEntries.bookkeepingAdjustment',
              'bookkeepingEntries.bookkeepingAdjustment.bookkeepingAccount',
              'bookkeepingEntries.flow',
              'bookkeepingEntries.document',
              'bookkeepingEntries.counterpart',
              'transactions',
              'vatRegistration',
              'vatRegistration.documents',
            ],
          })
        : null,
  });

const bookkeepingTransactionsDefaultSorting: SortInfo<BookkeepingTransactionSorting> = {
  sort: 'date',
  direction: 'asc',
};

export const useBookkeepingTransactions = (
  filters: BookkeepingTransactionFilters = {},
  sorting: SortInfo<BookkeepingTransactionSorting> = bookkeepingTransactionsDefaultSorting,
) => {
  const companyId = useActiveCompanyId();
  return useInfiniteQuery({
    queryKey: BookkeepingTransactionQueryKeys.list(companyId, filters, sorting),
    queryFn: ({ pageParam }) => fetchBookkeepingTransactions(companyId, filters, { cursor: pageParam, sorting }),
    getNextPageParam: ({ transactions, page }) => {
      if (transactions.length >= 50) {
        return page?.cursor;
      }
      return undefined;
    },
    initialPageParam: '',
  });
};

const decorateWithBalance = (
  transactions: BookkeepingTransaction[],
  initialBalance: Money,
  { accountId, counterpartId }: BookkeepingTransactionFilters = {},
): BookkeepingLedgerTransaction[] => {
  let balance = initialBalance;

  return transactions.map((transaction) => {
    const matchingEntries = transaction.bookkeepingEntries.filter((entry) => {
      const matchBookkeepingAccount = isDefined(accountId) ? entry.bookkeepingAccount.id === accountId : true;

      const matchCounterpart = isDefined(counterpartId)
        ? entry.counterpart?.id === counterpartId || entry.counterpart?.parent?.id == counterpartId
        : true;

      return matchBookkeepingAccount && matchCounterpart;
    });

    const balances: Money[] = [];
    for (const entry of matchingEntries) {
      if (isDare(entry)) {
        balance = sumMoney(balance, absMoney(entry.amount));
      } else if (isAvere(entry)) {
        balance = subtractMoney(balance, absMoney(entry.amount));
      }
      balances.push(balance);
    }

    return {
      ...transaction,
      bookkeepingEntries: matchingEntries,
      balances,
    };
  });
};

export const useBookkeepingTransactionsForLedgerCards = (
  section: LedgerCardsSection,
  filters: BookkeepingTransactionFilters = {},
  options?: {
    enabled: boolean;
  },
) => {
  const companyId = useActiveCompanyId();
  const enabled = isDefined(companyId) && (options?.enabled ?? true);

  const { data: initialBalance } = useQuery({
    queryKey: BookkeepingTransactionQueryKeys.metadata(companyId, filters, section),
    queryFn: async () => {
      const response = await fetchBookkeepingEntriesMetadata(companyId, filters);
      return response.startBalance;
    },
    enabled,
  });

  return useInfiniteQuery({
    queryKey: BookkeepingTransactionQueryKeys.listForSection(companyId, filters, section),
    queryFn: async ({ pageParam: { cursor, balance } }) => {
      const response = await fetchBookkeepingTransactions(companyId, filters, { cursor });

      return {
        transactions: decorateWithBalance(response.transactions, balance, filters),
        page: response.page,
      };
    },
    getNextPageParam: ({ transactions, page }) => {
      if (transactions.length >= 50) {
        return { cursor: page?.cursor || '', balance: last(last(transactions)!.balances)! };
      }
      return undefined;
    },
    initialPageParam: {
      cursor: '',
      balance: initialBalance || newMoney(0),
    },
    enabled: enabled && typeof initialBalance !== 'undefined',
    select: (data) => data.pages.flatMap((page) => page.transactions),
  });
};

export const useCreateBookkeepingTransaction = () => {
  const exceptionLogger = useExceptionLogger();
  const companyId = useActiveCompanyId();
  return useMutation({
    mutationFn: (values: Omit<CreateBookkeepingTransaction, 'companyId'>) =>
      createBookkeepingTransaction({
        ...values,
        companyId,
      }),
    onError: (error, values) => {
      exceptionLogger.captureException(error, {
        extra: {
          values,
        },
      });
    },
    onSuccess: (_data, values) => {
      queryClient.invalidateQueries({ queryKey: BookkeepingBankTransactionsKeys.all });
      queryClient.invalidateQueries({ queryKey: TransactionsKeys.all });
      queryClient.invalidateQueries({ queryKey: BookkeepingTransactionQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['Bookkeeping', 'documentsForTransactions'] });
      values.transactionIds.forEach((id) => {
        queryClient.invalidateQueries({ queryKey: BookkeepingBankTransactionsKeys.registrationProposal(id) });
      });
    },
  });
};

interface UpdateBookkeepingTransactionPayload {
  bookkeepingTransaction: BookkeepingTransaction;
  values: UpdateBookkeepingTransaction;
}

export const useUpdateBookkeepingTransaction = () => {
  const exceptionLogger = useExceptionLogger();
  return useMutation({
    mutationFn: ({ bookkeepingTransaction, values }: UpdateBookkeepingTransactionPayload) =>
      updateBookkeepingTransaction(bookkeepingTransaction.id, values),
    onError: (error, values) => {
      exceptionLogger.captureException(error, {
        extra: {
          values,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BookkeepingBankTransactionsKeys.all });
      queryClient.invalidateQueries({ queryKey: TransactionsKeys.all });
      queryClient.invalidateQueries({ queryKey: BookkeepingTransactionQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['Bookkeeping', 'documentsForTransactions'] });
    },
  });
};

export const useDeleteBookkeepingTransaction = () => {
  return useMutation({
    mutationFn: (bookkeepingTransaction: BookkeepingTransaction) =>
      deleteBookkeepingTransaction(bookkeepingTransaction.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BookkeepingTransactionQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: BookkeepingBankTransactionsKeys.allItems });
    },
  });
};

export const useBulkRegisterBookkeepingTransactions = () => {
  const exceptionLogger = useExceptionLogger();
  const companyId = useActiveCompanyId();
  return useMutation({
    mutationFn: (data: {
      transactionsFilters: TransactionsFiltersFields;
      attributes: BulkRegisterBookkeepingTransactionsPayload['attributes'];
    }) => {
      return bulkRegisterBookkeepingTransactions({ companyId, ...data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BookkeepingBankTransactionsKeys.all });
      queryClient.invalidateQueries({ queryKey: TransactionsKeys.all });
      queryClient.invalidateQueries({ queryKey: BookkeepingTransactionQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['Bookkeeping', 'documentsForTransactions'] });
    },
    onError: (error, { transactionsFilters, attributes }) => {
      exceptionLogger.captureException(error, {
        extra: {
          filters: JSON.stringify(transactionsFilters),
          formData: JSON.stringify(attributes),
        },
      });
    },
  });
};

export const useBulkAcceptBookkeepingTransactions = () => {
  const exceptionLogger = useExceptionLogger();
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: (transactions: BookkeepingBankTransaction[]) => {
      return bulkAcceptBookkeepingTransactions(companyId, transactions);
    },
    onSuccess: (_data, transactions) => {
      queryClient.invalidateQueries({ queryKey: BookkeepingBankTransactionsKeys.all });
      queryClient.invalidateQueries({ queryKey: TransactionsKeys.all });
      queryClient.invalidateQueries({ queryKey: BookkeepingTransactionQueryKeys.all });
      const proposalTransactionIds = transactions.flatMap(
        (transaction) => transaction.registrationProposal?.transactions.map((t) => t.id) ?? [],
      );
      proposalTransactionIds.forEach((id) => {
        queryClient.invalidateQueries({
          queryKey: BookkeepingBankTransactionsKeys.registrationProposal(id),
        });
      });
    },
    onError: (error, transactions) => {
      exceptionLogger.captureException(error, {
        extra: {
          transactions: JSON.stringify(transactions),
        },
      });
    },
  });
};
